type DatabaseStreamSnapshot = {
  isStreaming: boolean;
  operationType?: 'create' | 'edit';
};

export type DatabaseStreamListener = (state: DatabaseStreamSnapshot) => void;

import { DatabasesAIFCProcessor } from '@databases/ai-first-creation-token-processing';
import { MigrationManager } from '@databases/collab-migrations/migrations-manager';
import { collabStateManager } from '@databases/collab-state-manager';
import { logError } from '@databases/logs';
import type { Token } from '@databases/streaming-csv-parser';
import { StreamingCsvParser, TokenType } from '@databases/streaming-csv-parser';

type DatabaseStreamOptions = {
  shouldClearContent?: boolean;
  operationType?: 'create' | 'edit';
  rowRenderDelayMs?: number;
};

export class DatabaseStreamState {
  private static instance: DatabaseStreamState | undefined;

  private isStreaming = false;
  private operationType: 'create' | 'edit' = 'create';
  private currentGroupId: string | undefined;
  private listeners = new Set<DatabaseStreamListener>();
  private tokenQueue: Token[] = [];
  private processingPromise: Promise<void> | null = null;
  private csvParser: StreamingCsvParser | null = null;
  private aifcProcessor: DatabasesAIFCProcessor | null = null;
  private rowIdToEntryIdMappings: Map<string, string> = new Map();
  private rowRenderDelayMs = 1000;
  private executedInitialWait = false;

  static getInstance(): DatabaseStreamState {
    if (!DatabaseStreamState.instance) {
      DatabaseStreamState.instance = new DatabaseStreamState();
    }
    return DatabaseStreamState.instance;
  }

  public get isStreamingActive(): boolean {
    return this.isStreaming;
  }

  public get streamingGroupId(): string | undefined {
    return this.currentGroupId;
  }

  /**
   * Subscribe to streaming state updates.
   * Returns cleanup function
   */
  public subscribe(listener: DatabaseStreamListener): () => void {
    this.listeners.add(listener);
    listener({ isStreaming: this.isStreaming, operationType: this.operationType });
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    const snapshot: DatabaseStreamSnapshot = {
      isStreaming: this.isStreaming,
      operationType: this.operationType,
    };
    this.listeners.forEach((listener) => {
      try {
        listener(snapshot);
        // eslint-disable-next-line canvas/no-swallow-errors-in-catch
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logError('Error in DatabaseStreamState listener', error);
      }
    });
  }

  public processChunk(data: string): void {
    if (!this.isStreaming || !this.csvParser) {
      throw new Error('Streaming not active. Parser not initialized.');
    }

    this.csvParser.onReceiveChunk(data);

    if (!this.processingPromise) {
      this.processingPromise = this.processQueueAsync();
      this.processingPromise.finally(() => {
        this.processingPromise = null;
      });
    }
  }

  async waitForSettled() {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));
  }

  private async processQueueAsync(): Promise<void> {
    if (!this.executedInitialWait) {
      this.executedInitialWait = true;
      await this.waitForSettled();
    }
    let lastToken: Token | undefined;

    while (this.tokenQueue.length > 0) {
      const token = this.tokenQueue.shift();

      /**
       * ROW RENDER DELAY: To create visual separation and allow the colorful border animation
       * to be visible, we introduce a delay BEFORE processing VALUE tokens that follow an ADD_ENTRY or EDIT_ENTRY.
       * This ensures the row is visible with its animated border before the data fills in.
       */
      if (
        token?.tokenType === TokenType.VALUE &&
        (lastToken?.tokenType === TokenType.ADD_ENTRY ||
          lastToken?.tokenType === TokenType.EDIT_ENTRY)
      ) {
        await new Promise((resolve) => setTimeout(resolve, this.rowRenderDelayMs));
      }

      if (token && this.aifcProcessor) {
        try {
          this.aifcProcessor.onToken(token);
          // eslint-disable-next-line canvas/no-swallow-errors-in-catch
        } catch (error: unknown) {
          logError('Error processing token', new Error(String(error)));
        }
      }

      /**
       * COOPERATIVE YIELDING: Critical for responsive streaming experience
       *
       * Without this yield, the while loop would process ALL queued tokens synchronously,
       * blocking the JavaScript main thread and preventing:
       * - New processChunk() calls from executing (poor server response latency)
       * - UI updates and user interactions (app appears frozen)
       * - Network callbacks and other async operations (system degradation)
       *
       * Benefits of yielding:
       * - Immediate responsiveness to new incoming chunks from server
       * - Maintains interactive UI during database creation (progress, cancellation)
       * - Prevents browser tab from becoming unresponsive
       * - Allows other JavaScript operations to continue normally
       *
       * How it works:
       * 1. setTimeout(resolve, 0) schedules a macrotask for the next event loop cycle
       * 2. await suspends this async function and empties the call stack
       * 3. Event loop processes other pending tasks (new chunks, UI events, etc.)
       * 4. When setTimeout callback fires, it resumes this function to process next token
       *
       * This creates cooperative multitasking: process one token → yield control →
       * handle other work → resume processing. Essential for responsive streaming UX.
       * If we wish to improve performance, we can consider using web workers to process the tokens.
       */
      await new Promise((resolve) => setTimeout(resolve, 0));

      lastToken = token;
    }
  }

  public startStreaming(
    contentId: string,
    groupId?: string,
    options?: DatabaseStreamOptions
  ): void {
    const {
      shouldClearContent = false,
      operationType = 'create',
      rowRenderDelayMs = 1000,
    } = options || {};

    if (this.currentGroupId && this.currentGroupId !== groupId) {
      this.resetStreamingState();
    }

    this.isStreaming = true;
    this.operationType = operationType;
    this.currentGroupId = groupId;
    this.rowRenderDelayMs = rowRenderDelayMs;

    this.csvParser = new StreamingCsvParser((token) => {
      this.tokenQueue.push(token);
    }, operationType);

    const aifcProcessor = DatabasesAIFCProcessor.fromContentId(contentId);

    if (aifcProcessor) {
      if (shouldClearContent) {
        aifcProcessor.clearDatabase();
        this.setDatabaseVersionToSkipMigrations(contentId);
      }

      aifcProcessor.populateRowIdToEntryIdMappings(this.rowIdToEntryIdMappings);
    }

    this.aifcProcessor = aifcProcessor;
    this.notify();
  }

  private setDatabaseVersionToSkipMigrations(contentId: string): void {
    const collabManager = collabStateManager.getCollabManager(contentId);
    if (!collabManager) {
      return;
    }

    const clientDocVersion = MigrationManager.getClientDocVersion();
    const meta = collabManager.database.doc.getMap('_meta');
    meta.set('version', clientDocVersion);
  }

  public async completeStreaming(): Promise<void> {
    if (this.isStreaming) {
      this.processChunk('\n');
    }

    // Wait for all token processing to complete BEFORE setting isStreaming to false
    // This ensures rows keep their animated borders during the delay periods
    if (this.processingPromise) {
      await this.processingPromise;
    }

    // Now that all processing is done, we can set isStreaming to false
    this.isStreaming = false;

    if (this.aifcProcessor) {
      this.aifcProcessor.onStreamingEnd();
    }
    this.resetStreamingState();
    this.notify();
  }

  public populateRowIdToEntryIdMappings(mappings: Map<string, string>): void {
    this.rowIdToEntryIdMappings = mappings;
  }

  private resetStreamingState(): void {
    this.isStreaming = false;
    this.currentGroupId = undefined;
    this.csvParser = null;
    this.aifcProcessor = null;
    this.tokenQueue = [];
    this.processingPromise = null;
    this.rowRenderDelayMs = 1000;
  }
}
